/* eslint-disable @next/next/no-img-element */
import React, { useEffect, useState, useCallback } from 'react'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { Typography, Box, CircularProgress, useMediaQuery, Tooltip } from '@mui/material'
import Button from '@/src/components/button'
import CommonApi from '@/pages/api'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import TruncatedAnswer from '@/src/components/HomepageEdit/components/TruncatedElement'
import { formatDate } from '@/src/types/helper'
import { useRouter } from 'next/router'
import { AnnouncementGreyIcon } from '@/src/assets/icons'
import { useStore } from '@/src/types/store'
import { getAnnouncementUrl } from '@/src/types/helper'

const PdfViewer = dynamic(() => import('@/src/components/pdf-viewer'), { ssr: false })

interface AnnouncementsProps {
  sectionId: number
  isDraft?: boolean
}
const AnnouncementSection = ({ sectionId, isDraft = false }: AnnouncementsProps) => {
  const { TextColorType } = useStore()
  const [announcementSectionData, setAnnouncementSectionData] = useState<any>()
  const [loading, setLoading] = useState(false)
  const isAboveLg = useMediaQuery('(min-width:1280px)')
  const isBelowMd = useMediaQuery('(max-width:540px)')
  const router = useRouter()

  const fetchAnnouncementSectionData = useCallback(async () => {
    setLoading(true)
    try {
      const response = await CommonApi({
        method: 'get',
        path: `show-pinned-content/${sectionId}${isDraft ? '?status=draft' : ''}`
      })
      if (response.status) {
        setAnnouncementSectionData(response.data)
      } else {
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }, [sectionId, isDraft])

  useEffect(() => {
    fetchAnnouncementSectionData()
  }, [fetchAnnouncementSectionData])

  return (
    <Box className={`flex justify-center`}>
      {loading ? (
        <Box className="w-full flex justify-center items-center h-[300px]">
          <CircularProgress size={25} />
        </Box>
      ) : (
        <Box className="w-full flex flex-col xl:flex-row justify-center gap-0 lg:gap-14">
          {announcementSectionData?.pinned_announcements?.length > 0 && (
            <Box
              className={`w-full ${announcementSectionData?.pinned_presentations?.length > 0 && 'xl:w-3/5'} flex flex-col gap-6`}
            >
              <Box className="flex justify-between items-center">
                <Typography variant="h5" className="self-center">
                  Key announcements
                </Typography>
                <Box>
                  <Link href="/announcements?filter=All">
                    <Button
                      variant="outlined"
                      icon={<ArrowForwardIcon />}
                      iconPosition="right"
                      extraClass={`rounded-[100px] ${
                        TextColorType?.primaryTextDark
                          ? 'text-primary-home border-primary-home'
                          : TextColorType?.secondaryTextDark
                            ? 'text-secondary-home border-secondary-home'
                            : 'text-black border-black'
                      }`}
                      customStyle={true}
                      textChange={
                        <>
                          <span className="hidden sm:inline">More announcements</span>
                          <span className="inline sm:hidden">View more</span>
                        </>
                      }
                    />
                  </Link>
                </Box>
              </Box>
              <Box className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3 lg:mt-0 xl:h-[336px]">
                {announcementSectionData?.pinned_announcements?.map((val: any, idx: number) => (
                  <Box
                    key={idx}
                    onClick={() => router.push(getAnnouncementUrl(val.headline, val?.id))}
                    className="bg-white pt-4 px-6 pb-6 rounded-[20px] shadow-md cursor-pointer xl:h-[160px]"
                  >
                    <Box className="gap-1 items-center">
                      <Typography variant="caption">{formatDate(val.display_date, 'long')}</Typography>
                      <TruncatedAnswer maxLength={70} content={`${val.headline}`} textType={'subtitle1'} />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          {announcementSectionData?.pinned_presentations?.length > 0 && (
            <Box
              className={`w-full ${announcementSectionData?.pinned_announcements?.length > 0 && 'xl:w-2/5'} mt-6 lg:mt-0 flex flex-col gap-6`}
            >
              <Box className="flex justify-between items-center gap-2">
                <Typography variant="h5" className="whitespace-nowrap">
                  Latest presentations
                </Typography>
                <Box>
                  <Link href="announcements?filter=Presentation">
                    <Button
                      variant="outlined"
                      icon={<ArrowForwardIcon />}
                      iconPosition="right"
                      extraClass={`rounded-[100px] ${
                        TextColorType?.primaryTextDark
                          ? 'text-primary-home border-primary-home'
                          : TextColorType?.secondaryTextDark
                            ? 'text-secondary-home border-secondary-home'
                            : 'text-black border-black'
                      }`}
                      customStyle={true}
                      textChange={
                        <>
                          <span className="hidden xxxl:inline whitespace-nowrap">
                            More presentations
                          </span>
                          <span className="inline xxxl:hidden">View more</span>
                        </>
                      }
                    />
                  </Link>
                </Box>
              </Box>

              <Box className="flex flex-col gap-4 xl:h-[336px]">
                {announcementSectionData?.pinned_presentations?.map((val: any, index: number) => (
                  <Box
                    className="cursor-pointer"
                    key={index}
                    onClick={() => router.push(getAnnouncementUrl(val.headline, val?.id))}
                  >
                    <Box className="flex flex-col sm:flex-row gap-4 items-center sm:items-start border sm:border-none rounded-xl">
                      {/* Thumbnail Box */}
                      <Box
                        sx={{
                          borderRadius: '12px',
                          overflow: 'hidden',
                          aspectRatio: '16 / 9',
                          width: '100%',
                          maxWidth: '284.44px',
                          height: { sm: '160px', xs: 'auto' },
                          flexShrink: 0
                        }}
                      >
                        {val?.thumbnail ? (
                          <Box sx={{ width: '100%', height: '100%' }}>
                            <img
                              src={val?.thumbnail}
                              alt={val.headline}
                              style={{
                                objectFit: 'fill',
                                objectPosition: 'center',
                                width: '100%',
                                height: '100%'
                              }}
                            />
                          </Box>
                        ) : val?.file ? (
                          <Box sx={{ width: '100%', height: '100%' }}>
                            <PdfViewer
                              pdfFile={val.file}
                              customPage={1}
                              height={160}
                              width={284.44}
                            />
                          </Box>
                        ) : (
                          <Box className="w-full h-full bg-[#46586B4D] flex items-center justify-center">
                            <AnnouncementGreyIcon className="h-12 w-12" />
                          </Box>
                        )}
                      </Box>

                      {/* Text Section */}
                      <Box className="flex-1 flex flex-col gap-1 min-w-0 text-center sm:text-left mt-2 sm:mt-0">
                        <Typography variant="caption">{formatDate(val.display_date, 'long')}</Typography>
                        <Tooltip title={val?.headline} placement="top">
                          <Typography className=" line-clamp-3" variant="subtitle1">
                            {val?.headline}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}

export default AnnouncementSection
